<template>
  <v-dialog
    v-model="modelValue"
    :content-class="
      modelValue
        ? `max-w-dialog rounded-lg d-flex flex-column overflow-y-hidden ${isMobile ? 'mobile' : ''}`
        : `max-w-dialog rounded-lg overflow-y-hidden ${isMobile ? 'mobile' : ''}`
    "
    v-bind="isMobile ? { fullscreen: true, 'hide-overlay': true, transition: 'dialog-bottom-transition' } : {}"
  >
    <v-sheet
      color="white"
      class="d-flex flex-column rounded-lg max-h-full overflow-y-hidden"
      :class="{ 'h-100': isMobile }"
    >
      <v-form v-model="form.valid" ref="form" class="d-flex flex-column max-h-full h-100 overflow-y-hidden">
        <div class="d-flex flex-row align-center justify-space-between px-6 py-4">
          <span class="font-size-22"> Crie seu modelo </span>
          <v-btn icon color="black" @click="close">
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </div>
        <v-divider />
        <div
          class="max-h-full"
          :class="{ 'grid-55-45 overflow-y-hidden': !isMobile, 'h-100 overflow-y-auto': isMobile }"
        >
          <div class="d-flex flex-column pa-6 gap-6 overflow-y-auto">
            <the-alert v-if="!completedProfile" type="info" dismissible>
              Atualize seu perfil para que mais informações fiquem disponíveis para seu modelo.
            </the-alert>
            <div class="d-flex flex-column gap-2">
              <span class="font-weight-medium"> Selecione um tipo de documento </span>
              <v-radio-group v-model="form.fields.type" hide-details="always" class="ma-0 pa-0">
                <div class="d-flex flex-row gap-4 w-full">
                  <label
                    :class="[
                      'd-flex align-center pa-4 gap-4 border rounded-lg w-full template-radio p-relative',
                      {
                        active: form.fields.type === 'REPORT',
                        'justify-space-between': !isMobile,
                        'justify-center': isMobile,
                      },
                    ]"
                    role="button"
                    for="radio:report"
                  >
                    <div
                      class="d-flex align-center"
                      :class="{ 'flex-column gap-2': isMobile, 'flex-row gap-3': !isMobile }"
                    >
                      <report-icon
                        :style="`height: ${isMobile ? '32px' : '52px'}; width: ${isMobile ? '32px' : '52px'}`"
                      />
                      <span class="font-size-14 text-muted"> Relatório </span>
                    </div>
                    <v-radio
                      id="radio:report"
                      class="align-self-start ma-0"
                      :class="{ 'p-absolute top-3 right-3': isMobile }"
                      :disabled="locked && form.fields.type !== 'REPORT'"
                      value="REPORT"
                    />
                  </label>
                  <label
                    :class="[
                      'd-flex align-center pa-4 gap-4 border rounded-lg w-full template-radio p-relative',
                      {
                        active: form.fields.type === 'RECEIPT',
                        'justify-space-between': !isMobile,
                        'justify-center': isMobile,
                        'tw-cursor-not-allowed': locked && form.fields.type,
                      },
                    ]"
                    role="button"
                    for="radio:receipt"
                  >
                    <div
                      class="d-flex align-center"
                      :class="{ 'flex-column gap-2': isMobile, 'flex-row gap-3': !isMobile }"
                    >
                      <receipt-icon
                        :style="`height: ${isMobile ? '32px' : '52px'}; width: ${isMobile ? '32px' : '52px'}`"
                      />
                      <span class="font-size-14 text-muted"> Receita </span>
                    </div>
                    <v-radio
                      id="radio:receipt"
                      class="align-self-start ma-0"
                      :class="{ 'p-absolute top-3 right-3': isMobile }"
                      :disabled="locked && form.fields.type !== 'RECEIPT'"
                      value="RECEIPT"
                    />
                  </label>
                </div>
              </v-radio-group>
            </div>
            <v-text-field
              v-model="form.fields.name"
              class="new-input flex-grow-0"
              label="Nome do modelo*"
              outlined
              hide-details
              :rules="[$validation.required()]"
            />
            <v-file-input
              v-model="form.fields.clinicLogo"
              class="new-input flex-grow-0"
              label="Logo da clínica"
              outlined
              hide-details
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              accept="image/png,image/jpeg"
            />
            <div v-if="!isEmpty(formHeaderOptions)" class="d-flex flex-column gap-2">
              <div class="d-flex flex-row gap-2 color-464646">
                <v-icon dense> mdi-page-layout-header </v-icon>
                <span class="font-weight-medium"> Cabeçalho </span>
              </div>
              <v-select
                v-model="form.fields.header"
                :items="formHeaderOptions"
                class="new-input"
                outlined
                small-chips
                hide-details
                multiple
              />
            </div>
            <div v-if="!isEmpty(formFooterOptions)" class="d-flex flex-column gap-2">
              <div class="d-flex flex-row gap-2 color-464646">
                <v-icon dense> mdi-page-layout-footer </v-icon>
                <span class="font-weight-medium"> Rodapé </span>
              </div>
              <v-select
                v-model="form.fields.footer"
                :items="formFooterOptions"
                class="new-input"
                outlined
                small-chips
                hide-details
                multiple
              />
            </div>
            <div class="d-flex flex-row flex-wrap align-center gap-4">
              <v-select
                v-if="form.fields.footer.includes('physicianRqe')"
                v-model="form.fields.choices.footer.rqe"
                :items="physicianSpecialtiesWithRqe"
                class="new-input w-100 max-w-half"
                label="Selecionar RQE"
                item-value="id"
                item-text="name"
                outlined
                hide-details
                :rules="[$validation.required()]"
              />
              <v-select
                v-else-if="form.fields.footer.includes('physicianSpecialty')"
                v-model="form.fields.choices.footer.specialty"
                :items="physicianSpecialtiesWithoutRqe"
                class="new-input w-100 max-w-half"
                label="Selecionar especialidade"
                item-value="id"
                item-text="name"
                outlined
                hide-details
                :rules="[$validation.required()]"
              />
            </div>
          </div>
          <div
            v-if="!isMobile"
            class="d-flex flex-column align-center pa-4 bg-f1f3f4 max-h-full overflow-y-hidden gap-2"
          >
            <div class="d-flex flex-row align-center justify-space-between" :style="`width: ${preview.sizing.width}px`">
              <span class="font-weight-medium text-muted">Visualização</span>
              <v-btn icon color="gray" @click="printPreview" :disabled="!preview.pdf">
                <v-icon>mdi-printer-outline</v-icon>
              </v-btn>
            </div>
            <div
              ref="pdfContainer"
              class="d-flex flex-column h-full w-full align-center max-h-full overflow-y-auto py-2"
            >
              <v-sheet
                v-if="preview.loading"
                color="white"
                :style="pdfComputedSizeStyle"
                class="d-flex flex-column align-center justify-center elevation-3 aspect-ratio-a4"
              >
                <v-progress-circular indeterminate color="primary" />
              </v-sheet>
              <vue-pdf-embed
                v-else-if="preview.pdf"
                id="pdf-preview"
                class="pdf elevation-3"
                :style="pdfComputedSizeStyle"
                :source="preview.pdf"
              />
              <v-sheet v-else color="white" :style="pdfComputedSizeStyle" class="elevation-3 aspect-ratio-a4" />
            </div>
          </div>
        </div>
        <v-divider />
        <div class="d-flex flex-row align-center justify-end gap-4 py-4 px-6">
          <v-btn class="new-btn" outlined color="primary" @click="close"> Cancelar </v-btn>
          <v-btn
            class="new-btn"
            depressed
            color="primary"
            :disabled="!form.valid"
            :valid="form.loading"
            @click="edit ? updateTemplate() : createTemplate()"
          >
            {{ edit ? 'Atualizar modelo' : 'Criar modelo' }}
          </v-btn>
        </div>
      </v-form>
    </v-sheet>
  </v-dialog>
</template>

<script>
import { AsyncMethod, Computed, Method, Prop, WritableComputed } from '@/_typedefs'
import { PrintingTemplate, PrintingTemplateType } from '@/plugins/api/endpoints/printingTemplate'
import { useV2Api } from '@/plugins/api/openapi'
import { useAuthStore } from '@/store/pinia/auth'
import { useAlert } from '@/v2/composables/alert/alert'
import { isEmpty, isNil } from 'lodash'
import printJS from 'print-js'
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
import TheAlert from '../TheAlert.vue'
import ReceiptIcon from './CustomIcons/ReceiptIcon.vue'
import ReportIcon from './CustomIcons/ReportIcon.vue'

const api = useV2Api()

export default {
  components: { ReportIcon, ReceiptIcon, TheAlert, VuePdfEmbed },
  props: {
    /** @type {Prop<boolean>} */
    value: {
      type: Boolean,
      required: true,
    },
    /** @type {Prop<PrintingTemplate>} */
    template: {
      type: Object,
      default: null,
    },
    /** @type {Prop<boolean>} */
    edit: {
      type: Boolean,
      default: false,
    },
    /** @type {Prop<PrintingTemplateType>} */
    type: {
      type: String,
      default: 'REPORT',
      validator: (val) => ['REPORT', 'RECEIPT'].includes(val),
    },
    locked: {
      type: Boolean,
    },
  },
  data() {
    return {
      physician: {},
      completedProfile: false,
      preview: {
        /** @type {string} */
        pdf: null,
        loading: false,
        sizing: {
          /** @type {number?} */
          height: null,
          /** @type {number?} */
          width: null,
        },
        /** @type {ResizeObserver?} */
        resizeObserver: null,
      },
      form: {
        fields: {
          /** @type {PrintingTemplateType} */
          type: this.type,
          /** @type {string?} */
          name: null,
          /** @type {File?} */
          clinicLogo: null,
          /** @type {string[]} */
          header: [],
          /** @type {string[]} */
          footer: [],
          choices: {
            footer: {
              rqe: null,
              specialty: null,
              address: null,
            },
          },
        },
        options: {
          /** @type {Record<string, {text: string, value: string}[]>} */
          footer: {
            REPORT: [
              { text: 'Data de emissão', value: 'date' },
              { text: 'Nome do médico', value: 'physicianName' },
              { text: 'Especialidade', value: 'physicianSpecialty' },
              { text: 'CRM', value: 'physicianCrm' },
              { text: 'RQE', value: 'physicianRqe' },
            ],
            RECEIPT: [
              { text: 'Nome do médico', value: 'physicianName' },
              { text: 'Especialidade', value: 'physicianSpecialty' },
              { text: 'CRM', value: 'physicianCrm' },
              { text: 'RQE', value: 'physicianRqe' },
            ],
          },
          /** @type {Record<string, {text: string, value: string}[]>} */
          header: {
            REPORT: [],
            RECEIPT: [
              { text: 'Nome do paciente', value: 'patientName' },
              { text: 'CPF', value: 'patientCpf' },
              { text: 'Data de nascimento', value: 'patientBirthday' },
            ],
          },
        },
        valid: false,
        loading: false,
      },
    }
  },
  computed: {
    /** @type {WritableComputed<boolean>} */
    modelValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    /** @type {Computed<boolean>} */
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    /** @type {Computed<{id: number, address: string}[]>} */
    physicianAddresses() {
      return (
        this.physician.addresses
          ?.filter((address) => !!address.phone)
          .map((address) => ({ id: address.id, address: address.name })) ?? []
      )
    },
    /** @type {Computed<{id: number, name: string}[]>} */
    physicianSpecialtiesWithRqe() {
      return (
        this.physician.specialties
          ?.filter((specialty) => !isNil(specialty.rqe))
          .map((specialty) => ({ id: specialty.id, name: `${specialty.rqe} - ${specialty.type.name}` })) ?? []
      )
    },
    /** @type {Computed<{id: number, name: string}[]>} */
    physicianSpecialtiesWithoutRqe() {
      return (
        this.physician.specialties
          ?.filter((specialty) => isNil(specialty.rqe))
          .map((specialty) => ({ id: specialty.id, name: specialty.type.name })) ?? []
      )
    },
    /** @type {Computed<{value: string, text: string}[]>} */
    formFooterOptions() {
      let options = this.form.options.footer[this.form.fields.type] ?? []

      if (!isEmpty(options) && isEmpty(this.physicianSpecialtiesWithRqe)) {
        options = options.filter((option) => option.value !== 'physicianRqe')
      }

      if (!isEmpty(options) && isEmpty(this.physicianSpecialtiesWithoutRqe)) {
        options = options.filter((option) => option.value !== 'physicianSpecialty')
      }

      return options
    },
    /** @type {Computed<{value: string, text: string}[]>} */
    formHeaderOptions() {
      return this.form.options.header[this.form.fields.type] ?? []
    },
    /** @type {Computed<string?>} */
    pdfComputedSizeStyle() {
      if (!this.preview.sizing.height || !this.preview.sizing.width) {
        return null
      }

      return `
        height: ${this.preview.sizing.height}px;
        width: ${this.preview.sizing.width}px;
      `
    },
    printTemplate() {
      return this.$util.copy(this.template)
    },
  },
  methods: {
    /** @type {Method} */
    close() {
      this.modelValue = false

      setTimeout(() => {
        this.preview.pdf = null
        this.form.fields = {
          type: 'REPORT',
          name: null,
          clinicLogo: null,
          header: [],
          footer: [],
          choices: {
            footer: {
              rqe: null,
              specialty: null,
              address: null,
            },
          },
        }
        this.$refs.form.resetValidation()
        this.$emit('close')
      }, 200)
    },
    parseFormFieldsForCreate() {
      return {
        name: this.form.fields.name,
        type: this.form.fields.type,
        physicianId: this.authStore.userId,
        include: {
          date: this.form.fields.footer.includes('date'),
          patientBirthday: this.form.fields.header.includes('patientBirthday'),
          patientCpf: this.form.fields.header.includes('patientCpf'),
          patientName: this.form.fields.header.includes('patientName'),
          physicianCrm: this.form.fields.footer.includes('physicianCrm'),
          physicianName: this.form.fields.footer.includes('physicianName'),
          physicianPhone: this.form.fields.footer.includes('physicianPhone'),
          physicianRqe: this.form.fields.footer.includes('physicianRqe'),
          physicianSpecialty: this.form.fields.footer.includes('physicianSpecialty'),
        },
        choices: {
          chosenAddress: this.form.fields.choices.footer.address,
          chosenSpecialty: this.form.fields.choices.footer.rqe ?? this.form.fields.choices.footer.specialty,
        },
        clinicLogo: this.form.fields.clinicLogo,
      }
    },
    parseFormFieldsForUpdate() {
      return {
        name: this.form.fields.name,
        type: this.form.fields.type,
        include: {
          date: this.form.fields.footer.includes('date'),
          patientBirthday: this.form.fields.header.includes('patientBirthday'),
          patientCpf: this.form.fields.header.includes('patientCpf'),
          patientName: this.form.fields.header.includes('patientName'),
          physicianCrm: this.form.fields.footer.includes('physicianCrm'),
          physicianName: this.form.fields.footer.includes('physicianName'),
          physicianPhone: this.form.fields.footer.includes('physicianPhone'),
          physicianRqe: this.form.fields.footer.includes('physicianRqe'),
          physicianSpecialty: this.form.fields.footer.includes('physicianSpecialty'),
        },
        choices: {
          chosenAddress: this.form.fields.choices.footer.address,
          chosenSpecialty: this.form.fields.choices.footer.rqe ?? this.form.fields.choices.footer.specialty,
        },
        clinicLogo: this.form.fields.clinicLogo,
      }
    },
    /** @type {AsyncMethod} */
    async generatePreview() {
      if (this.preview.loading === true) {
        return
      }

      try {
        this.preview.loading = true
        const blob = await api.medicalDocumentTemplate.generatePreview(this.parseFormFieldsForCreate())
        this.preview.pdf = URL.createObjectURL(blob)
      } catch (error) {
        this.alert.error({ error })
      } finally {
        this.preview.loading = false
      }
    },
    /** @type {AsyncMethod} */
    async createTemplate() {
      try {
        this.form.loading = true
        const createdTemplate = await api.medicalDocumentTemplate.create(this.parseFormFieldsForCreate())
        this.close()
        if (!this.edit) {
          this.alert.ok({ message: 'Novo modelo criado' })
        }
        this.$emit('created', createdTemplate)
      } catch (error) {
        this.alert.error({ error })
      } finally {
        this.form.loading = false
      }
    },
    /** @type {AsyncMethod} */
    async updateTemplate() {
      try {
        this.form.loading = true
        const updatedTemplate = await api.medicalDocumentTemplate.update(
          this.template.id,
          this.parseFormFieldsForUpdate(),
        )
        this.close()
        this.$emit('updated', updatedTemplate)
      } catch (error) {
        this.alert.error({ error })
      } finally {
        this.form.loading = false
      }
    },
    /** @type {Method} */
    printPreview() {
      printJS({
        printable: document.querySelector('#pdf-preview canvas').toDataURL(),
        type: 'image',
        style: 'width: 100%',
      })
    },
    /** @type {AsyncMethod} */
    async fetchPhysicianData() {
      try {
        const physician = await api.physician.get(this.authStore.userId)
        this.physician = physician

        this.completedProfile = [
          !!physician.name,
          !!physician.gender,
          !!physician.birthday,
          !!physician.phone,
          !!physician.crms?.at(0),
          !!physician.cns,
          !!physician.specialties?.at(0),
          !!physician.addresses?.at(0),
        ].every((condition) => condition === true)
      } catch (error) {
        console.error(error)
      }
    },
    isEmpty,
  },
  watch: {
    'form.fields.type'() {
      if (!this.preview.pdf) {
        return
      }
      const formHeaderOptionsValues = this.formHeaderOptions.map(({ value }) => value)
      const formFooterOptionsValues = this.formFooterOptions.map(({ value }) => value)

      this.form.fields.header = this.form.fields.header.filter((header) => formHeaderOptionsValues.includes(header))
      this.form.fields.footer = this.form.fields.footer.filter((footer) => formFooterOptionsValues.includes(footer))
    },
    'form.fields.clinicLogo'() {
      if (!this.preview.pdf) {
        return
      }
      this.generatePreview()
    },
    'form.fields.header': {
      handler() {
        if (!this.preview.pdf) {
          return
        }
        this.generatePreview()
      },
      deep: true,
    },
    'form.fields.footer': {
      handler() {
        if (!this.preview.pdf) {
          return
        }
        this.generatePreview()
      },
      deep: true,
    },
    'form.fields.choices': {
      handler() {
        if (!this.preview.pdf) {
          return
        }
        this.generatePreview()
      },
      deep: true,
    },
    modelValue: {
      handler(val) {
        if (!val) {
          this.preview.resizeObserver.disconnect()
          return
        }

        this.$nextTick(() => {
          this.preview.resizeObserver.observe(this.$refs.pdfContainer)
          if (!this.preview.pdf) {
            this.generatePreview()
          }
        })
      },
    },
    printTemplate: {
      handler(val) {
        if (!val) {
          return
        }
        let clinicLogoFile = null
        if (val.clinicLogo) {
          clinicLogoFile = this.$util.dataURLtoFile(val.clinicLogo, 'logo-clinica')
        }

        const headerInclude = {
          patientBirthday: val.include.patientBirthday,
          patientCpf: val.include.patientCpf,
          patientName: val.include.patientName,
        }

        const footerInclude = {
          date: val.include.date,
          physicianCrm: val.include.physicianCrm,
          physicianName: val.include.physicianName,
          physicianPhone: val.include.physicianPhone,
          physicianRqe: val.include.physicianRqe,
          physicianSpecialty: val.include.physicianSpecialty,
        }

        this.form.fields = {
          type: val.type,
          name: this.edit ? val.name : `${val.name} - Cópia`,
          header: Object.keys(headerInclude).filter((header) => !!headerInclude[header]),
          footer: Object.keys(footerInclude).filter(
            (footer) => !!footerInclude[footer] && !!this.formFooterOptions.find((option) => option.value === footer),
          ),
          clinicLogo: clinicLogoFile,
          choices: {
            footer: {
              rqe: val.choices.rqe,
              specialty: val.choices.specialty,
              address: val.choices.address,
            },
          },
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.preview.resizeObserver = new ResizeObserver((entries) => {
      const pdfContainer = entries.find((entry) => entry.target === this.$refs.pdfContainer)
      if (pdfContainer) {
        this.preview.sizing.height = Math.floor(pdfContainer.contentRect.height)
        this.preview.sizing.width = Math.floor(pdfContainer.contentRect.height / Math.SQRT2)
      }
    })

    if (this.modelValue) {
      this.preview.resizeObserver.observe(this.$refs.pdfContainer)
    }
  },
  beforeDestroy() {
    this.preview.resizeObserver.disconnect()
  },
  async created() {
    await this.fetchPhysicianData()
    if (this.value) {
      await this.generatePreview()
    }
  },
  setup() {
    const authStore = useAuthStore()
    const alert = useAlert()

    return {
      authStore,
      alert,
    }
  },
}
</script>

<style scoped>
:deep(.max-w-dialog) {
  max-width: min(1040px, 90%);
  width: 100%;
  overflow-y: unset;
}

:deep(.max-w-dialog.mobile) {
  max-width: 100%;
}

.max-w-half {
  max-width: calc(50% - 8px);
}

.max-h-dialog-content {
  max-height: calc(100% - 100px);
  height: 100%;
  overflow-y: auto;
}

.pdf :deep(canvas) {
  height: 100% !important;
  width: 100% !important;
  aspect-ratio: 1 / sqrt(2);
}

.aspect-radio-a4 {
  aspect-ratio: 1 / sqrt(2);
}

.grid-55-45 {
  display: grid;
  grid-template-columns: 55fr 45fr;
  height: 100%;
}

.new-alert.v-alert :deep(.v-alert__wrapper) {
  align-items: flex-start;
}
:deep(.new-alert.v-alert .v-alert__dismissible) {
  margin: 0;
}

:deep(.v-radio .v-input--selection-controls__input) {
  margin: 0;
}

.template-radio.active {
  border-color: var(--v-primary-base);
  box-shadow: inset 0 0 0 1px var(--v-primary-base);
}

.color-464646,
.color-464646 i {
  color: #464646;
}

.bg-f1f3f4 {
  background-color: #f1f3f4;
}

:deep(.v-chip.v-chip--select) {
  color: #666666;
}
</style>
